import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { roleIcon, roleLabel } from "./roles";
import { useHomeRole } from "./use-home-role";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuLinkItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, LoadingDotsIcon } from "mds/icons";
import { GENERAL_EVENT_CATEGORY } from "utils/amplitude";
import { homeUrlForRole } from "utils/urls";

export type RoleSelectProps = {
  className?: string;
  isLoading?: boolean;
};

export const RoleSelect: FC<RoleSelectProps> = ({ className, isLoading }) => {
  const { currentRole, availableRoles } = useHomeRole();
  const hasChoices = availableRoles.length > 1;

  const MenuButton = (
    <SquareButton
      className={twMerge(isLoading && "relative", className)}
      disabled={isLoading || !hasChoices}
      eventName="Switch User role"
      kind="secondary"
      size="s"
      title={t("home.select_role")}
    >
      {roleIcon[currentRole]}
      <span>{roleLabel[currentRole]}</span>
      {hasChoices && <ChevronDownIcon />}
      {isLoading && <LoadingDotsIcon className="absolute left-0 top-0 h-full w-full" />}
    </SquareButton>
  );

  if (isLoading) {
    return <div className="relative">{MenuButton}</div>;
  }

  if (!hasChoices) {
    return (
      <IconText
        className="text-sm font-medium text-black-tint-20"
        iconStart={roleIcon[currentRole]}
        text={roleLabel[currentRole]}
      />
    );
  }

  return (
    <Menu align="start" menuButton={MenuButton} title={t("home.header.org_select")}>
      {availableRoles.map((role) => (
        <MenuLinkItem
          eventName="New user role selected"
          eventProps={{ ...GENERAL_EVENT_CATEGORY, newRole: role }}
          key={role}
          selected={currentRole === role}
          to={homeUrlForRole(role)}
        >
          {roleIcon[role]}
          {roleLabel[role]}
        </MenuLinkItem>
      ))}
    </Menu>
  );
};
